import { useMemo } from 'react';
import {
  hasRole,
  isSystemAdmin,
  isSalesRep,
  isCaseManager,
  isSecretary,
  isSupervisor,
  getCurrentUserRole,
  getCurrentUserId
} from '../utils/authUtils';
import { getCurrentUser, isAuthenticated as checkAuthenticated } from '../services/authService';
import type { User } from '../services/authService';

interface UseAuthReturn {
  // User information
  currentUser: User | null;
  currentUserId: number | null;
  currentUserRole: string | null;
  isAuthenticated: boolean;

  // Role checking functions
  hasRole: (requiredRoles: string[]) => boolean;
  isSystemAdmin: boolean;
  isSalesRep: boolean;
  isCaseManager: boolean;
  isSecretary: boolean;
  isSupervisor: boolean;

  // Permission checking utilities
  canAccessAdminFeatures: boolean;
  canManageAllLeads: boolean;
  canManageAllCases: boolean;

  // Ownership helpers — for Secretary scoping in UI conditional rendering
  // (Backend is the source of truth — these are only for show/hide of buttons)
  canAccessLead: (leadId: number) => boolean;
  canAccessClient: (clientId: number) => boolean;
}

/**
 * Custom hook for authentication and authorization
 * Provides user information and role-based permission checking
 */
export const useAuth = (): UseAuthReturn => {
  const currentUser = getCurrentUser();
  const currentUserId = getCurrentUserId();
  const currentUserRole = getCurrentUserRole();
  const isAuthenticated = checkAuthenticated();

  // Memoize role checks for performance
  const roleChecks = useMemo(() => ({
    isSystemAdmin: isSystemAdmin(),
    isSalesRep: isSalesRep(),
    isCaseManager: isCaseManager(),
    isSecretary: isSecretary(),
    isSupervisor: isSupervisor()
  }), []);

  // Derived permissions
  const permissions = useMemo(() => ({
    canAccessAdminFeatures: roleChecks.isSystemAdmin,
    canManageAllLeads: roleChecks.isSystemAdmin || (roleChecks.isSalesRep && roleChecks.isSupervisor),
    canManageAllCases: roleChecks.isSystemAdmin || (roleChecks.isCaseManager && roleChecks.isSupervisor)
  }), [roleChecks]);

  // Ownership helpers — Secretary sees only assigned leads/clients (admin/supervisor sees all)
  const canAccessLead = (leadId: number): boolean => {
    if (roleChecks.isSystemAdmin || roleChecks.isSupervisor) return true;
    if (!roleChecks.isSecretary) return true; // SalesRep handles assignment elsewhere
    return currentUser?.assignedLeadIds?.includes(leadId) ?? false;
  };

  const canAccessClient = (clientId: number): boolean => {
    if (roleChecks.isSystemAdmin || roleChecks.isSupervisor) return true;
    if (!roleChecks.isSecretary) return true; // CaseManager handles assignment elsewhere
    return currentUser?.assignedClientIds?.includes(clientId) ?? false;
  };

  return {
    // User information
    currentUser,
    currentUserId,
    currentUserRole,
    isAuthenticated,

    // Role checking functions
    hasRole,
    isSystemAdmin: roleChecks.isSystemAdmin,
    isSalesRep: roleChecks.isSalesRep,
    isCaseManager: roleChecks.isCaseManager,
    isSecretary: roleChecks.isSecretary,
    isSupervisor: roleChecks.isSupervisor,

    // Permission checking utilities
    canAccessAdminFeatures: permissions.canAccessAdminFeatures,
    canManageAllLeads: permissions.canManageAllLeads,
    canManageAllCases: permissions.canManageAllCases,

    canAccessLead,
    canAccessClient
  };
};

export default useAuth;